import React from "react";
import { Link } from "react-router-dom";

/**
 * ErrorBoundary globale — intercetta qualsiasi runtime error nei discendenti
 * e mostra un fallback gentile invece della "schermata rossa" del bundler dev
 * (in dev) o di una pagina bianca (in prod).
 *
 * Tre layer:
 *  1. **Cattura** errori sync nei render e nei lifecycle (componentDidCatch).
 *  2. **Mostra** un messaggio amichevole con bottoni "Ricarica la pagina" /
 *     "Torna alla home". Nessun canale di supporto telefonico esposto.
 *  3. **Logga** discreto a console + opzionale POST /api/client-error per
 *     analytics (non blocking, fire-and-forget).
 *
 * Non gestiamo errori async / promise rejection — quelli arrivano a `toast`
 * via axios interceptor o try/catch locali. Qui è solo l'ultima rete.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log a console per debug locale
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info?.componentStack);
    // Fire-and-forget al backend per tracking (silently swallow se non disponibile)
    try {
      const url = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api/client-error";
      const body = {
        message: String(error?.message || error),
        stack: String(error?.stack || ""),
        componentStack: String(info?.componentStack || "").slice(0, 4000),
        path: typeof window !== "undefined" ? window.location.pathname : "",
        ua: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 200) : "",
      };
      if (typeof fetch === "function") {
        fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
          keepalive: true,
        }).catch(() => {});
      }
    } catch { /* silent */ }
  }

  handleReload = () => {
    if (typeof window !== "undefined") window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    // iter v29 r232 — User M1389: bug Luca Bertozzi «registrato, errore al
    // caricamento». Era HMR transient su BottomTabBar (Can't find variable).
    // Aggiunta variante `silent`: invece di prendere tutto lo schermo, on
    // error ritorna null (o fallback custom). Utile per wrappare singoli
    // componenti non-critici (es. BottomTabBar, sidebar widget) senza
    // distruggere l'intera dashboard se uno di loro va in fumo.
    if (this.props.silent) {
      return this.props.fallback ?? null;
    }

    const isDev = process.env.NODE_ENV === "development";

    return (
      <div
        className="min-h-screen bg-[#FAFAF7] flex items-center justify-center px-4 py-10"
        data-testid="error-boundary-fallback"
      >
        <div className="max-w-md w-full text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#F1EEE6] mb-5">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#7C8B6E" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          </div>
          <h1 className="font-editorial text-3xl text-[#1C1C1A] mb-3">Ops, qualcosa non ha funzionato.</h1>
          <p className="text-[14px] text-[#6B6A65] leading-relaxed mb-6">
            Un componente di questa pagina è andato in errore. I tuoi dati sono al sicuro — riprova ora,
            oppure torna alla home e apri di nuovo l{"'"}immobile.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2.5">
            <button
              onClick={this.handleReload}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 bg-[#1C1C1A] hover:bg-black text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-colors"
              data-testid="error-boundary-reload"
            >
              Ricarica la pagina
            </button>
            <Link
              to="/dashboard"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 bg-white border border-[#E5E7E0] hover:border-[#7C8B6E] text-[#1C1C1A] px-5 py-2.5 rounded-full text-sm font-semibold transition-colors"
              data-testid="error-boundary-home"
            >
              Torna alla home
            </Link>
          </div>

          {isDev && this.state.error && (
            <details className="mt-8 text-left bg-[#FFF5F5] border border-red-200 rounded p-3" data-testid="error-boundary-details">
              <summary className="text-[11px] font-semibold text-red-800 cursor-pointer">
                Dettaglio errore (visibile solo in dev)
              </summary>
              <pre className="mt-2 text-[10.5px] text-red-900 whitespace-pre-wrap break-words overflow-x-auto max-h-60">
                {String(this.state.error?.message || this.state.error)}
                {"\n\n"}
                {String(this.state.error?.stack || "").split("\n").slice(0, 8).join("\n")}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
